/**
 * Delivery saga functions
 */
import { all, call, put, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import Actions from './actions';
import Constants from '../../constants';
import BookingAPI from '../../Lib/Api/booking';
import UserAPI from '../../Lib/Api/user';
import VendorAPI from '../../Lib/Api/vendor.ts';
import { handleError } from '../../Lib/Utils/saga-utils';
import LocationAPI from '../../Lib/Api/location';
import BookingTypes from '../../Lib/Constants/booking-types';
import TypedBookingsAPI from '../../Lib/Api/bookings.ts';

const {
  Deliveries,
  Delivery,
  Location,
  LocationHour,
  Locations,
  PurchaseOrders,
  Vendor,
  Block,
} = Constants;

/**
 * Creates a delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* createDelivery({ delivery, purchaseOrderNumbers, cb }) {
  try {
    const response = yield call(
      BookingAPI.createDelivery,
      delivery,
      purchaseOrderNumbers,
    );
    yield put(Actions.createDeliverySuccess(response));
    if (cb) yield call(cb());
  } catch (err) {
    yield handleError(Actions.createDeliveryError, err);
  }
}

/**
 * Creates delivery and dispatches success or error
 *
 * @param {object} action
 * {
 *   @param {string} userId the user id
 *   @param {object} delivery the delivery data
 *   @param {array} purchaseOrderIds the purchase order numbers to be delivered
 * }
 */
export function* createVendorDelivery({
  userId,
  delivery,
  purchaseOrderNumbers,
}) {
  try {
    const response = yield call(
      UserAPI.createVendorDelivery,
      userId,
      delivery,
      purchaseOrderNumbers,
    );
    yield put(Actions.createVendorDeliverySuccess(response));
  } catch (err) {
    yield handleError(Actions.createVendorDeliveryError, err);
  }
}

/**
 * Creates location hour and dispatches success or error
 *
 * @param {object} action
 * {
 *   @param {string} locationId the location id
 *   @param {object} hour the hour data
 * }
 */
export function* createLocationHour({ locationId, hour }) {
  try {
    const response = yield call(
      LocationAPI.createLocationHour,
      locationId,
      hour,
    );
    yield put(Actions.createLocationHourSuccess(response));
  } catch (err) {
    yield handleError(Actions.createLocationHourError, err);
  }
}

/**
 * Deletes location hour and dispatches success or error
 *
 * @param {object} action
 * {
 *   @param {string} locationId the location id
 *   @param {object} hourId the hour id
 * }
 */
export function* deleteLocationHour({ locationId, hourId }) {
  try {
    const response = yield call(
      LocationAPI.deleteLocationHour,
      locationId,
      hourId,
    );
    yield put(Actions.deleteLocationHourSuccess(response));
  } catch (err) {
    yield handleError(Actions.deleteLocationHourError, err);
  }
}

/**
 * fetches a delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* fetchDelivery({ deliveryId }) {
  try {
    const response = yield call(BookingAPI.getOneDelivery, deliveryId);
    yield put(Actions.getDeliverySuccess(response));
  } catch (err) {
    yield handleError(Actions.getDeliveryError, err);
  }
}

/**
 * fetches all deliveries
 *
 * @param {object} action action object containing type and payload properties
 */
export function* fetchDeliveries({ options }) {
  try {
    const response = yield call(BookingAPI.get, options);
    yield put(Actions.getDeliveriesSuccess(response));
  } catch (err) {
    yield handleError(Actions.getDeliveriesError, err);
  }
}

/**
 * gets all blocks
 *
 * @param {object} action action object containing type and payload properties
 */
export function* getBlocks({ options }) {
  try {
    const response = yield call(BookingAPI.get, options);
    yield put(Actions.getBlocksSuccess(response));
  } catch (err) {
    yield handleError(Actions.getBlocksError, err);
  }
}

/**
 * Gets the deliveries associated with the user
 *
 * @param {string} userId the user id
 */
export function* getVendorDeliveries({ userId, params }) {
  try {
    const response = yield call(UserAPI.getVendorDeliveries, userId, params);
    yield put(Actions.getVendorDeliveriesSuccess(response));
  } catch (err) {
    yield handleError(Actions.getVendorDeliveriesError, err);
  }
}

/**
 * Gets location hour and dispatches success or error
 *
 * @param {object} action
 * {
 *   @param {string} locationId the location id
 *   @param {object} hourId the hour id
 * }
 */
export function* getLocationHour({ locationId, hourId }) {
  try {
    const response = yield call(
      LocationAPI.getLocationHour,
      locationId,
      hourId,
    );
    yield put(Actions.getLocationHourSuccess(response));
  } catch (err) {
    yield handleError(Actions.getLocationHourError, err);
  }
}

/**
 * updates a delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* updateDelivery({
  deliveryId,
  delivery,
  purchaseOrderNumbers,
  cb,
}) {
  try {
    const response = yield call(
      BookingAPI.updateDelivery,
      deliveryId,
      delivery,
      purchaseOrderNumbers,
    );
    yield put(Actions.updateDeliverySuccess(response));
    if (cb) yield call(cb());
  } catch (err) {
    yield handleError(Actions.updateDeliveryError, err);
  }
}

/**
 * updates a delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* checkInDelivery({ deliveryId, deliveredAt, cb }) {
  try {
    const response = yield call(
      TypedBookingsAPI.checkInDelivery,
      deliveryId,
      deliveredAt,
    );
    yield put(Actions.updateDeliverySuccess(response));
    if (cb) yield call(cb());
  } catch (err) {
    yield handleError(Actions.updateDeliveryError, err);
  }
}

/**
 * Gets location hour and dispatches success or error
 *
 * @param {object} action
 * {
 *   @param {string} locationId the location id
 *   @param {object} hourId the hour id
 * }
 */
export function* updateLocationHour({ locationId, hourId, hour }) {
  try {
    const response = yield call(
      LocationAPI.updateLocationHour,
      locationId,
      hourId,
      hour,
    );
    yield put(Actions.updateLocationHourSuccess(response));
  } catch (err) {
    yield handleError(Actions.updateLocationHourError, err);
  }
}

/**
 * Gets the specified delivery
 *
 * @param {string} userId the user id
 * @param {string} deliveryId the delivery id
 */
export function* getVendorDelivery({ userId, deliveryId }) {
  try {
    const response = yield call(UserAPI.getVendorDelivery, userId, deliveryId);
    yield put(Actions.getVendorDeliverySuccess(response));
  } catch (err) {
    yield handleError(Actions.getVendorDeliveryError, err);
  }
}

/**
 * removes a delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* removeDelivery({ deliveryId, reason }) {
  try {
    yield call(BookingAPI.removeDelivery, deliveryId, reason);
    yield put(Actions.removeDeliverySuccess(deliveryId));
  } catch (err) {
    yield handleError(Actions.removeDeliveryError, err);
  }
}

/**
 * removes a vendor's delivery
 *
 * @param {object} action action object containing type and payload properties
 */
export function* removeVendorDelivery({ userId, deliveryId, reason }) {
  try {
    yield call(UserAPI.removeVendorDelivery, userId, deliveryId, reason);
    yield put(Actions.removeVendorDeliverySuccess(deliveryId));
  } catch (err) {
    yield handleError(Actions.removeVendorDeliveryError, err);
  }
}

/**
 * Creates a location
 *
 * @param {object} action action object containing type and payload properties
 */
export function* createLocation({ location }) {
  try {
    const response = yield call(LocationAPI.create, location);
    yield put(Actions.createLocationSuccess(response));
  } catch (err) {
    yield handleError(Actions.createLocationError, err);
  }
}

/**
 * fetches a location
 *
 * @param {object} action action object containing type and payload properties
 */
export function* fetchLocation({ locationId }) {
  try {
    const response = yield call(LocationAPI.getOne, locationId);
    yield put(Actions.getLocationSuccess(response));
  } catch (err) {
    yield handleError(Actions.getLocationError, err);
  }
}

/**
 * fetches all deliveries
 *
 * @param {object} action action object containing type and payload properties
 */
export function* fetchLocations() {
  try {
    const response = yield call(LocationAPI.get);
    yield put(Actions.getLocationsSuccess(response));
  } catch (err) {
    yield handleError(Actions.getLocationsError, err);
  }
}

/**
 * updates a location
 *
 * @param {object} action action object containing type and payload properties
 */
export function* updateLocation({ locationId, location }) {
  try {
    const response = yield call(LocationAPI.update, locationId, location);
    yield put(Actions.updateLocationSuccess(response));
  } catch (err) {
    yield handleError(Actions.updateLocationError, err);
  }
}

/**
 * removes a location
 *
 * @param {object} action action object containing type and payload properties
 */
export function* removeLocation({ locationId }) {
  try {
    const response = yield call(LocationAPI.remove, locationId);
    yield put(Actions.removeLocationSuccess(response));
  } catch (err) {
    yield handleError(Actions.removeLocationError, err);
  }
}

/**
 * Fetches deliverable purchase orders
 *
 * @param {object} action action object containing type and payload properties
 */
export function* fetchDeliverablePurchaseOrders({
  id,
  locationId,
  isVendorId,
  params,
}) {
  try {
    const method = isVendorId
      ? VendorAPI.getDeliverablePurchaseOrders
      : UserAPI.getDeliverablePurchaseOrders;
    const response = yield call(method, id, locationId, params);

    yield put(Actions.getDeliverablePurchaseOrdersSuccess(response));
  } catch (err) {
    yield handleError(Actions.getDeliverablePurchaseOrdersError, err);
  }
}

/**
 * Fetches availabilities for deliveries.
 *
 * @param {object} action action object
 * containing type and payload properties
 */
export function* fetchAvailabilities({ options }) {
  try {
    const response = yield call(BookingAPI.getDeliveryAvailabilities, options);
    yield put(Actions.getAvailabilitiesSuccess(response));
  } catch (err) {
    yield handleError(Actions.getAvailabilitiesError, err);
  }
}

/**
 * Create a block booking.
 *
 * @param {object} action action object
 * containing type and payload properties
 */
export function* createBlock({ block, cb }) {
  try {
    const { booking } = yield call(BookingAPI.createBlock, block);
    const options = {
      type: [BookingTypes.BLOCK, BookingTypes.ORDER_DELIVERY],
      location_id: booking.location_id,
      date: moment(booking.starts_at).startOf('day').toDate().toISOString(),
    };
    const response = yield call(BookingAPI.get, options);
    yield put(Actions.createBlockSuccess(response));
    if (cb) yield call(cb());
  } catch (err) {
    yield handleError(Actions.createBlockError, err);
  }
}

/**
 * Get a block booking.
 *
 * @param {object} action action object
 * containing type and payload properties
 */
export function* getBlock({ blockId }) {
  try {
    const response = yield call(BookingAPI.getBlock, blockId);
    yield put(Actions.getBlockSuccess(response));
  } catch (err) {
    yield handleError(Actions.getBlockError, err);
  }
}

/**
 * Updates a block booking.
 *
 * @param {object} action action object
 * containing type and payload properties
 */
export function* updateBlock({ block, blockId, cb }) {
  try {
    const { booking } = yield call(BookingAPI.updateBlock, block, blockId);
    const options = {
      type: [BookingTypes.BLOCK, BookingTypes.ORDER_DELIVERY],
      location_id: booking.location_id,
      date: moment(booking.starts_at).startOf('day').toDate().toISOString(),
    };
    const response = yield call(BookingAPI.get, options);
    yield put(Actions.updateBlockSuccess(response));
    if (cb) cb();
  } catch (err) {
    yield handleError(Actions.updateBlockError, err);
  }
}

/**
 * Deletes a block booking.
 *
 * @param {object} action action object
 * containing type and payload properties
 */
export function* removeBlock({ blockId }) {
  try {
    yield call(BookingAPI.removeBlock, blockId);
    yield put(Actions.removeBlockSuccess(blockId));
  } catch (err) {
    yield handleError(Actions.removeBlockError, err);
  }
}

/**
 * Updates the specific delivery
 *
 * @param {string} userId the user id
 * @param {string} deliveryId the delivery id
 * @param {object} update the update data
 */
export function* updateVendorDelivery(action) {
  const { userId, deliveryId, update, purchaseOrderNumbers } = action;
  try {
    const response = yield call(
      UserAPI.updateVendorDelivery,
      userId,
      deliveryId,
      update,
      purchaseOrderNumbers,
    );
    yield put(Actions.updateVendorDeliverySuccess(response));
  } catch (err) {
    yield handleError(Actions.updateVendorDeliveryError, err);
  }
}

function* deliverySaga() {
  yield all([
    yield takeLatest(Vendor.CREATE_VENDOR_DELIVERY, createVendorDelivery),
    yield takeLatest(Vendor.GET_VENDOR_DELIVERIES, getVendorDeliveries),
    yield takeLatest(Vendor.GET_VENDOR_DELIVERY, getVendorDelivery),
    yield takeLatest(Vendor.UPDATE_VENDOR_DELIVERY, updateVendorDelivery),
    yield takeLatest(Vendor.REMOVE_VENDOR_DELIVERY, removeVendorDelivery),
    yield takeLatest(Delivery.CREATE_DELIVERY, createDelivery),
    yield takeLatest(Delivery.GET_DELIVERY, fetchDelivery),
    yield takeLatest(Deliveries.GET_DELIVERIES, fetchDeliveries),
    yield takeLatest(Delivery.UPDATE_DELIVERY, updateDelivery),
    yield takeLatest(Delivery.CHECK_IN_DELIVERY, checkInDelivery),
    yield takeLatest(Delivery.REMOVE_DELIVERY, removeDelivery),
    yield takeLatest(Location.CREATE_LOCATION, createLocation),
    yield takeLatest(Location.GET_LOCATION, fetchLocation),
    yield takeLatest(Locations.GET_LOCATIONS, fetchLocations),
    yield takeLatest(Location.UPDATE_LOCATION, updateLocation),
    yield takeLatest(Location.REMOVE_LOCATION, removeLocation),
    yield takeLatest(
      PurchaseOrders.GET_DELIVERABLE_PURCHASE_ORDERS,
      fetchDeliverablePurchaseOrders,
    ),
    yield takeLatest(Delivery.GET_AVAILABILITIES, fetchAvailabilities),
    yield takeLatest(Block.CREATE_BLOCK, createBlock),
    yield takeLatest(Block.GET_BLOCK, getBlock),
    yield takeLatest(Block.UPDATE_BLOCK, updateBlock),
    yield takeLatest(Block.REMOVE_BLOCK, removeBlock),
    yield takeLatest(LocationHour.CREATE_LOCATION_HOUR, createLocationHour),
    yield takeLatest(LocationHour.GET_LOCATION_HOUR, getLocationHour),
    yield takeLatest(LocationHour.UPDATE_LOCATION_HOUR, updateLocationHour),
    yield takeLatest(LocationHour.DELETE_LOCATION_HOUR, deleteLocationHour),
  ]);
}

export default deliverySaga;
